import { FC, useEffect, useMemo } from 'react';

import * as Yup from 'yup'
import {Formik, Form, FormikProps, FormikValues, FieldArray} from 'formik'

import { 
  FormikTextInput, FormikCheckBox, FormikFileUpload, FormikInspectorAgreeCheck, FormikDateSelector,   
  billingDetailsSchema, FormikBillingDetails, initBillingDetails, ParseBillingFields, BillMode,   
  StatusErrorMessage, 
  FormikErrorMessage, FormikNotesArea 
} from '../../../../../components/widgets';
import { doFormSubmit, isPopulatedArray, processNoteValues, reloadDraftVars } from '../../../../../components/helpers';
import { PhaseFormObserver } from '../../components/PhaseFormObserver';
import { cloneDeep, pick, pickBy } from 'lodash';
import { Application } from '../../../../core/applications';
import { SubmitButtonRow } from '../../../../../components/layout';

import { useQueryClient } from 'react-query';
import { KTSVG } from '../../../../../../_metronic/helpers';


const samplingResultSchema = Yup.object({
  id: Yup.number(),
  surveillanceAttachmentId: Yup.number().integer().moreThan(0, 'Surveillance Test Result required').required().label('Surveillance Test Result'),
  surveillanceAttachmentIdAttachmentName: Yup.string().trim(),
  testReportReference: Yup.string().trim().required().label('Test Report Reference'),
});

const inspectorSamplingSchema = Yup.object({
  verdict: Yup.string().trim(),
  approved: Yup.boolean(),
  reviewId: Yup.number(),
  testDate: Yup.string().trim().label('Test Date'),
  majorNCFound: Yup.boolean(),
  samplings: Yup.array().label('Samplings')
  .when("verdict", {
    is: (verdict: string) => (verdict === 'approved' || verdict === 'rejected'),
    then: (schema) => schema.of(samplingResultSchema).min(1, 'Surveillance Test Result(s) required').required()
  }),  
  rescheduleDate: Yup.string().trim().label('Reschedule Date')
  .when("verdict", {
    is: (verdict: string) => (verdict === 'reskedSampling'),
    then: (schema) => schema.required('Reschedule Date required.'),
  }),
  ncrAttachmentId: Yup.number().integer().label('Non-Conformity Report')
  .when("verdict", {
    is: (verdict: string) => (verdict === 'rejected'),
    then: (schema) => schema.min(1, 'Non-Conformity Report required').required()
  }),
  ncrAttachmentIdAttachmentName: Yup.string().trim(),
  samplingFormId: Yup.number().integer().label('Sampling Form')
  .when("verdict", {
    is: (verdict: string) => (['approved','rejected'].includes(verdict)),
    then: (schema) => schema.moreThan(0,'Upload Sampling Form').required(),
  }),
  samplingFormIdAttachmentName: Yup.string().trim(),
  note: Yup.string().trim().label('Notes')
  .when("verdict", {
    is: (verdict: string) => (verdict !== 'approved'),
    then: (schema) => schema.required('Details required.').label('Notes'),
  }),
  noteAttachments: Yup.array(),
  inspectorAgree: Yup.bool().label('Confirmation')
  .when("verdict", {
    is: (verdict: string) => (verdict === 'approved'),
    then: (schema) => schema.oneOf([true], 'Must be checked'),
  }),
}).concat(billingDetailsSchema);


const initSamplingResultValues = {
  id: 0,
  surveillanceAttachmentId: 0,
  surveillanceAttachmentIdAttachmentName: '',
  testReportReference: '',
};

type FormProps = {
  formikProps: FormikProps<any>
  verdict: string
  doClose: Function
}


const InspectorSamplingComponentForm =( {formikProps, verdict, doClose}: FormProps) => {

  const {isSubmitting, values, status, setFieldValue, validateForm} = formikProps;
  const { reviewId, verdict: verdictValue } = values;

  useEffect(() => {        
    setFieldValue('verdict', verdict);
    setFieldValue('approved', (verdict === 'approved'))
        
    const billMode: BillMode = ['approved','rejected'].includes(verdict) ? 'YES' : 'NO';    
    setFieldValue('billingRequired', billMode);
  
    // Functional updater receives the current values safely
    setFieldValue('transports', (prevValues: any) => {
      const transports = prevValues?.transports ?? [];
      
      if (isPopulatedArray(transports)) {
        return transports.map((o: any) => ({ ...o, billingRequired: billMode }));
      }
      
      return transports;
    });
        
  }, [verdict, setFieldValue]);

  useEffect(() => {
    validateForm();
  }, [verdictValue, validateForm])

  return (
  <>
  <PhaseFormObserver reviewId={reviewId} values={values} isSubmitting={isSubmitting} verdict={verdict} />
  <Form id='kt_inspector_sampling_form' className='form' noValidate>
    <div
      className='d-flex flex-column gap-4 mb-6 px-2'
      id='kt_inspector_sampling_scroll'
      data-kt-scroll='true'
      data-kt-scroll-activate='{default: false, lg: true}'
      data-kt-scroll-max-height='auto'
      data-kt-scroll-dependencies='#kt_inspector_sampling_header'
      data-kt-scroll-wrappers='#kt_inspector_sampling_scroll'
      data-kt-scroll-offset='300px'
    >
      { (verdict === 'approved' || verdict === 'rejected') &&
      <>
      <div className="row mb-2">
        <FormikFileUpload size="12 col-md-6" label="Sampling Form" name='samplingFormId' acceptFiles="UserFileFormats" required />      
        { verdict === 'rejected' &&       
        <FormikFileUpload size="12 col-md-6" label="Non-Conformity Report" name='ncrAttachmentId' acceptFiles="UserFileFormats" required />
        }
        { verdict === 'approved' &&
        <div className="12 col-md-6 mt-4">          
          <FormikCheckBox size="12" name="majorNCFound" label="Major Non-Conformity Found" />
        </div>
        }
      </div>          
      <div className='row mb-2'>
        <FormikTextInput size="6" label="Date of Surveillance Test" name="testDate" />
      </div>
      <FieldArray
        name="samplings"
        render={ rowHelpers => (
          <>
          <div className="row pe-8">
            <div className="col-6 fs-6 fw-bold">Surveillance Test Result</div>
            <div className="col-5 fs-6 fw-bold">Test Report Reference</div>
            <div className="col-1 text-center">
              <button
                type="button"
                className="siteBtn btn btn-light-success text-success btn-active-success px-2 py-1 lh-0 mx-auto"
                onClick={() => rowHelpers.push( { ...initSamplingResultValues } )}
                title="Click to add Test Result"
              >
                <KTSVG path='/media/icons/duotune/arrows/arr017.svg' className='svg-icon-4 m-0' />
              </button>
            </div>
          </div>
          <div className='separator border-gray-200'></div>
          <div className="scroll-y mh-150px">
            { (values?.samplings) && values.samplings.map((row: any, idx: number) => (
            <div key={`sample${idx}`} className="row mb-2 mr-2">
              <FormikFileUpload size="6" label="Surveillance Test Result" name={`samplings.${idx}.surveillanceAttachmentId`} acceptFiles="UserFileFormats" required />
              <FormikTextInput size="5" label="Test Report Reference" name={`samplings.${idx}.testReportReference`} required/>
              <div className="col-1 text-center">
                <div
                  className="btn btn-light-danger text-danger btn-active-danger px-1 py-1 lh-0 text-center"                
                  onClick={ () => rowHelpers.remove(idx)}
                  title="Delete Test Result"
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr018.svg' className='svg-icon-4 mx-1' />
                </div>
              </div>
            </div>
            ))}
          </div>
          <FormikErrorMessage name="samplings" />          
          </>
        )}
      />
      </>
      }

      <FormikBillingDetails />

      { (verdict === 'reskedSampling') &&
      <div className="row mb-2">
        <FormikDateSelector size="12" label="Reschedule Date" name="rescheduleDate" required={true} showShiftSelector={true} dateFormat='LL' asRow />
      </div>
      }

      <FormikNotesArea label={(verdict === 'adminReport' || verdict === 'reskedSampling') ? "Note" : "Reason"} required={verdict !== 'approved'} />
     
      { verdict === 'approved' &&
      <FormikInspectorAgreeCheck />
      }

      <StatusErrorMessage status={status} />
      <SubmitButtonRow doClose={doClose} />
    </div>
  </Form>
  </>
  )
}

type Props = {
  appData: Application
  reviewId: number
  reviewState: string
  draftVars: Record<string, any>
  verdict: string
  doClose: Function
}

const InspectorSamplingForm : FC<Props> = ({ appData, reviewId, reviewState, draftVars, verdict, doClose }) => {

  const queryClient = useQueryClient();

  const initValues = useMemo(() => {

    let initValues = {
      verdict: '',
      approved: false,
      reviewId,
      state: reviewState,

      ncrAttachmentId: 0,
      ncrAttachmentIdAttachmentName: '',
      samplingFormId: 0,
      samplingFormIdAttachmentName: '',
      majorNCFound: false,
      testDate: '',
      testReportReference: '',
      inspectorAgree: false,      
      rescheduleDate: '',
      
      samplings: [{ ...initSamplingResultValues } ],
      
      ...cloneDeep(initBillingDetails),

      note: '',
      noteAttachments: [],
    };

    
    reloadDraftVars(initValues, draftVars, ['verdict','reviewId','approved','state','inspectorAgree']);

    return initValues;

  }, [reviewId, reviewState, draftVars]);


  const doSubmit = async (values: any, actions: FormikValues) => {
    const { setStatus, setSubmitting } = actions;
    // exclude empty fields, attachment names and booleans

    let approved = (verdict === 'approved') ? true : false;
    let payload = {
      ...pickBy(values, (value, key) => (
        value &&
        (! (key.match(/attachmentName$/i) || (['verdict','state','inspectorAgree','samplingFormId','ncrAttachmentId','testDate','samplings'].includes(key))))
      )),
      approved,
      majorNCFound: false
    };

    if (approved && values?.majorNCFound) {
      if (! window.confirm("Major Non-Conformity Found! Do you still want to proceed?")) {
        setSubmitting(false);
        return;
      }
      Object.assign(payload, { majorNCFound: values.majorNCFound });
    }

    switch(verdict) {
      case 'pendingApplicant':
        Object.assign(payload, { pendingReport: true });
        break;
      case 'pendingAdmin':
        Object.assign(payload, { pendingReport: true, toAdmin: true });
        break;
      case 'reschedule':
      case 'reskedSampling':
        Object.assign(payload, { reschedule: true });
        break;
      case 'approved':
      case 'rejected':
        Object.assign(payload, pickBy(values, (value, key) =>  (value && ['samplingFormId','ncrAttachmentId','testDate'].includes(key))));
        if (isPopulatedArray(values?.samplings)) {
          Object.assign(payload, { samplings : values.samplings.map( (o: any) => (pick(o, [ 'id', 'surveillanceAttachmentId', 'testReportReference' ])))})
        }
        break;
      default:
        break;
    }

    payload = ParseBillingFields(payload, values);
    processNoteValues(values, payload);

    await doFormSubmit('Phase_5_2Servlet', payload, setStatus, setSubmitting, () => {
      queryClient.invalidateQueries(['APPLICATIONS-LIST']);
      queryClient.invalidateQueries(['GETAPPLICATION', `${appData.id}`]);
      doClose();
    });
  }

  return (
  <Formik
    initialValues={initValues}
    validationSchema={inspectorSamplingSchema}
    enableReinitialize={true}
    onSubmit={doSubmit}
  >
  {(formikProps) => (
    <InspectorSamplingComponentForm formikProps={formikProps} verdict={verdict} doClose={doClose} />
  )}
  </Formik>
  )
}


export {InspectorSamplingForm}
